import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  PARTNER_ADD_DEVICE_TOTAL_STEPS,
  partnerAddDeviceStepMapping,
  partnerAddDeviceStepPaths,
  partnerAddDeviceSubsteps,
} from "@/apps/partner/utils/partnerAddDevice.constants";
import { CheckCircle, MenuPartner } from "@/assets/icons/icons";
import { Button } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import { StepMenuItem } from "@/components/Interface/Stepper/StepMenu";
import { StepFlowLayout } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowLayout";
import { StepFlowNavigation } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowNavigation";

type SuccessCustomerType = "new" | "existing";

const ORDER_SUCCESS_KEY = "orderSuccess";
const ORDER_SUCCESS_CUSTOMER_TYPE_KEY = "orderSuccessCustomerType";

const Success = () => {
  const { t: tSuccess } = useTranslation("partnerPortal", {
    keyPrefix: "success",
  });
  const { t } = useTranslation("partnerPortal", { keyPrefix: "addDevice" });
  const navigate = useNavigate();
  const [customerType, setCustomerType] = useState<SuccessCustomerType | null>(
    null,
  );

  useEffect(() => {
    const hasValidAccess = sessionStorage.getItem(ORDER_SUCCESS_KEY) === "true";
    const storedCustomerType = sessionStorage.getItem(
      ORDER_SUCCESS_CUSTOMER_TYPE_KEY,
    );

    if (!hasValidAccess) {
      navigate("/partner/add-sim", { replace: true });

      return;
    }

    sessionStorage.removeItem(ORDER_SUCCESS_KEY);
    sessionStorage.removeItem(ORDER_SUCCESS_CUSTOMER_TYPE_KEY);
    setCustomerType(storedCustomerType === "existing" ? "existing" : "new");
  }, [navigate]);

  const menuItems: StepMenuItem[] = partnerAddDeviceSubsteps.map((stepKey) => {
    const stepMeta = partnerAddDeviceStepMapping[stepKey];

    return {
      label: t(`steps.${stepMeta.label}` as "steps.sim-type.label"),
      path: partnerAddDeviceStepPaths[stepKey],
      disabled: true,
    };
  });

  if (!customerType) {
    return null;
  }

  const copyKeyPrefix =
    customerType === "existing" ? "existingCustomer" : "newCustomer";

  return (
    <StepFlowLayout
      dataTestid="partner-add-device-success"
      title={t("title")}
      step={PARTNER_ADD_DEVICE_TOTAL_STEPS}
      totalSteps={PARTNER_ADD_DEVICE_TOTAL_STEPS}
      stepLabel={t("steps.personal-details.label" as "steps.sim-type.label")}
      menuItems={menuItems}
      menuToggleLabel={t("stepMenuLabel")}
    >
      <div className="flex w-full flex-col gap-10">
        <div className="flex flex-col items-center gap-6 text-center">
          <CheckCircle className="size-14 text-foreground-success" />
          <p className="text-body-base font-semibold text-foreground-primary">
            {tSuccess("successMessage")}
          </p>
        </div>

        <section className="flex flex-col gap-4">
          <h2 className="text-body-base font-semibold text-foreground-primary">
            {tSuccess(`${copyKeyPrefix}.whatHappensNext.title`)}
          </h2>
          <p className="text-body-base text-foreground-primary">
            {tSuccess(`${copyKeyPrefix}.whatHappensNext.subtitle`)}
          </p>
          <ul className="flex list-disc flex-col gap-1 pl-8 text-body-base text-foreground-primary">
            <li>{tSuccess(`${copyKeyPrefix}.whatHappensNext.list.1`)}</li>
            <li>{tSuccess(`${copyKeyPrefix}.whatHappensNext.list.2`)}</li>
            <li>{tSuccess(`${copyKeyPrefix}.whatHappensNext.list.3`)}</li>
          </ul>
        </section>

        <section className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <MenuPartner className="size-6 shrink-0 text-foreground-primary" />
            <h2 className="text-body-base font-semibold text-foreground-primary">
              {tSuccess(`${copyKeyPrefix}.partnerSupport.title`)}
            </h2>
          </div>
          <p className="text-body-base text-foreground-primary">
            {tSuccess(`${copyKeyPrefix}.partnerSupport.subtitle`)}
          </p>
          <ol className="flex list-decimal flex-col gap-1 pl-8 text-body-base text-foreground-primary">
            <li>{tSuccess(`${copyKeyPrefix}.partnerSupport.list.1`)}</li>
            <li>{tSuccess(`${copyKeyPrefix}.partnerSupport.list.2`)}</li>
            <li>{tSuccess(`${copyKeyPrefix}.partnerSupport.list.3`)}</li>
            {customerType === "new" && (
              <li>{tSuccess("newCustomer.partnerSupport.list.4")}</li>
            )}
          </ol>
          <Notification
            description={tSuccess(`${copyKeyPrefix}.partnerSupport.hint`)}
          />
        </section>

        <StepFlowNavigation
          primaryButton={
            <Button
              variant="primary"
              onClick={() => navigate("/partner/add-sim")}
              dataTestid="add-more-plans-button"
            >
              {tSuccess("addMorePlans")}
            </Button>
          }
          secondaryButton={
            <Button
              variant="secondary"
              onClick={() => navigate("/partner/cockpit")}
            >
              {tSuccess("back")}
            </Button>
          }
        />
      </div>
    </StepFlowLayout>
  );
};

export default Success;
